import { computed, ref } from 'vue';
import { useUploadAndCreateCarDocument } from '@/hooks/use-upload-and-create-car-document';
import { useExtractRegistrationCertificate } from '@/queries/use-cars';
import { CarDocumentType } from '@/entities/cars/car-document-type.enum';
import type { RegistrationCertificateExtractionDto } from '@carvia/ros-client-types';

export type RegistrationCertificateUploadStatus =
  | 'idle'
  | 'uploading'
  | 'extracting'
  | 'done'
  | 'upload_error'
  | 'extraction_error';

export const useUploadAndExtractRegistrationCertificate = () => {
  const status = ref<RegistrationCertificateUploadStatus>('idle');
  const result = ref<RegistrationCertificateExtractionDto | null>(null);
  const uploadedDocumentId = ref<string | null>(null);

  const { uploadAndCreateCarDocument } = useUploadAndCreateCarDocument();
  const { mutateAsync: extractCertificate } = useExtractRegistrationCertificate();

  const isLoading = computed(() => status.value === 'uploading' || status.value === 'extracting');

  const isUploading = computed(() => status.value === 'uploading');
  const isExtracting = computed(() => status.value === 'extracting');
  const isDone = computed(() => status.value === 'done');
  const hasExtractionError = computed(() => status.value === 'extraction_error');
  const hasUploadError = computed(() => status.value === 'upload_error');

  /**
   * Uploads the file as a REGISTRATION_CERTIFICATE document, then calls the
   * backend extraction endpoint to read the FIN and licence plate via AI.
   *
   * Returns the extraction result if successful, or null if either step fails.
   * Upload and extraction errors are tracked separately so the UI can show
   * targeted feedback.
   */
  const uploadAndExtract = async (
    carId: string,
    file: File,
    documentName: string = 'Fahrzeugschein',
  ): Promise<RegistrationCertificateExtractionDto | null> => {
    status.value = 'uploading';
    result.value = null;
    uploadedDocumentId.value = null;

    try {
      const doc = await uploadAndCreateCarDocument(
        carId,
        file,
        CarDocumentType.REGISTRATION_CERTIFICATE,
        documentName,
      );
      uploadedDocumentId.value = doc.id;
    } catch {
      status.value = 'upload_error';
      return null;
    }

    status.value = 'extracting';
    try {
      const extracted = await extractCertificate(carId);
      result.value = extracted;
      status.value = 'done';
      return extracted;
    } catch {
      // Upload succeeded but extraction failed — the document is in the DB.
      // We surface a non-blocking error so the user can fill in FIN/plate manually.
      status.value = 'extraction_error';
      return null;
    }
  };

  return {
    uploadAndExtract,
    status,
    result,
    uploadedDocumentId,
    isLoading,
    isUploading,
    isExtracting,
    isDone,
    hasExtractionError,
    hasUploadError,
  };
};
